import { Component, DestroyRef, inject, OnDestroy, OnInit } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { addIcons } from "ionicons";
import { add, chevronForwardOutline } from "ionicons/icons";
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	switchMap,
	take,
	takeUntil,
	tap,
} from "rxjs/operators";
import moment from "moment";
import { combineLatest, EMPTY, merge, Subject } from "rxjs";

import { AWSHttpClient } from "@intocare-medapp/aws/http";
import { IcOrganisation } from "@intocare-medapp/organisation/ic-organisation";
import { IcEnvironment } from "@intocare-medapp/environment";

import { DynamicFormBuilder } from "app/shared/modules/ngx-dynamic-forms";

import {
	CheckClientService,
	ContextService,
	DebugReduxStore,
	IcAppState,
	IcDeepLinking,
	LanguageService,
	LocalStorageService,
	NFCService,
} from "../shared";
import { SyncModel } from "../shared/offline";
import {
	BackButtonService,
	CordovaService,
	NFCEvent,
	SimpleToastService,
	SplashScreenService,
} from "../shared/modules";
import { matchNFCToPatient } from "../shared/utils/match-nfc";
import { DCustomFormControl } from "../shared/modules/ngx-dynamic-forms/entities";
import * as FiltersActions from "../shared/state/actions/data/action-creators/filters.actions";
import { AnnouncementsModel } from "../shared/models/announcements.model";
import { DialogComponent } from "../shared/components/dialogs/dialog/dialog.component";
import {
	selectAnnouncementByVersion,
	selectLatestUnnanouncedAnnouncement,
} from "../shared/state/selectors/announcements.selectors";
import { AnnouncementsActions } from "../shared/state/actions/data/action-creators/announcements.action";
import { Announcement } from "app/shared/state/reducers/data/announcements.reducer";
import { IcEvents } from "app/ic-events/ic-events.service";
import { MatomoService } from "../shared/services";
import { EVENT_SERVICE } from "@intocare-medapp/events";

declare const window: any;

window.reload = () => {
	const environment = new IcEnvironment().getEnvironment();
	window.navigator.splashscreen.show();
	window.location = environment.CORDOVA.CDV_FULL_URL;
};

/**
 * This class represents the main application component. Within the @Routes annotation is the configuration of the
 * applications routes, configuring the paths for the lazy loaded components (HomeComponent, AboutComponent).
 */
@Component({
	selector: "sd-app",
	templateUrl: "app.component.html",
	styleUrls: ["app.component.scss"],
	standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly icAppState = inject(IcAppState);
	private readonly icOrganisation = inject(IcOrganisation);
	private readonly cordova = inject(CordovaService);
	private readonly splashscreen = inject(SplashScreenService);
	private readonly backbutton = inject(BackButtonService);
	private readonly icEvents = inject(IcEvents);
	private readonly syncModel = inject(SyncModel);
	private readonly toast = inject(SimpleToastService);
	private readonly translateService = inject(TranslateService);
	private readonly languageService = inject(LanguageService);
	private readonly store = inject(Store);
	private readonly router = inject(Router);
	private readonly icDeepLinking = inject(IcDeepLinking);
	private readonly nfc = inject(NFCService);
	private readonly awsHttpClient = inject(AWSHttpClient);
	private readonly fb = inject(DynamicFormBuilder);
	private readonly dialog = inject(MatDialog);
	private readonly debugReduxStore = inject(DebugReduxStore);
	private readonly icEnvironment = inject(IcEnvironment);
	private readonly announcementsModel = inject(AnnouncementsModel);
	private readonly localStorageService = inject(LocalStorageService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly checkClientService = inject(CheckClientService);
	private readonly matomoService = inject(MatomoService);
	private readonly environment = this.icEnvironment.getEnvironment();
	private readonly notAllowedUrls = [
		"/user-briefing",
		"/auth/sign-in-with-username",
		"/home/announcements",
		"/filters?page=user-briefing&redirectUrl=%2Fuser-briefing",
	];
	private readonly contextService = inject(ContextService);
	private readonly eventService = inject(EVENT_SERVICE);

	isAuthenticated = this.contextService.isAuthenticated;
	isAuthenticated$ = toObservable(this.isAuthenticated);

	constructor() {
		addIcons({ add, chevronForwardOutline });
	}

	private handleIncorrectAppState() {
		this.icAppState.setAppInError();
		this.router.navigate(["/something-went-wrong"]);
	}

	ngOnInit() {
		// Set startuptime in localStorage
		this.localStorageService.setAppStartTime(moment().format());
		// Set the start time in unix format
		this.localStorageService.setAppStartTimeUnix(`${Date.now()}`);
		// Initialize the splashscreen service (fallback for when hide is not called)
		this.splashscreen.init();
		// Intercept the HW back button. Exits the app if no handler is provided.
		this.backbutton.intercept();

		// Listener for Deep link events and act when a deep link is received
		this.icDeepLinking.init();

		this.icEvents.fatalReducerError$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.handleIncorrectAppState());

		// Start the syncing process
		this.syncModel.sync();

		// Start the polling of the new offline event syncing flow
		this.eventService.init();

		this.checkClientService.checkClient().subscribe();

		// Listen to changes in the organisation and update the HTTP base url
		this.icOrganisation.organisation$
			.pipe(
				filter<string>(Boolean),
				distinctUntilChanged(),
				tap((organisation) => this.awsHttpClient.setBaseUrl(`${this.environment.AWS.API.URL}/${organisation}`)),
			)
			.subscribe();

		// Update the dynamic form configuration
		this.translateService.onLangChange
			.pipe(
				startWith({ lang: this.languageService.language() }),
				tap((value) => this.fb.setLanguage(value.lang)),
			)
			.subscribe();

		this.store
			.select((state) => state.data.evaluationforms.configs)
			.pipe(
				filter<DCustomFormControl[]>(Boolean),
				tap((controls) => this.fb.setCustomControls(controls)),
			)
			.subscribe();

		this.showResidentsForNfcTagReads().subscribe();

		this.store
			.select(selectLatestUnnanouncedAnnouncement)
			.pipe(
				filter(Boolean),
				switchMap((announcement: Announcement) => {
					/**
					 * If we are currently on a route where we can show the dialog, show it
					 * If we are not on a route where we can show the dialog, wait until we are on a route where we can show the dialog
					 * But check if the announcement is still not read/announced, if it is, don't show the dialog
					 */
					if (this.notAllowedUrls.includes(this.router.url)) {
						return this.router.events.pipe(
							filter((event) => event instanceof NavigationEnd),
							map((event: NavigationEnd) => event.url),
							filter((url) => !this.notAllowedUrls.includes(url)),
							switchMap(() => {
								return this.store.select(selectAnnouncementByVersion(announcement.version)).pipe(
									// @ts-expect-error TS2345
									switchMap((announcement: Announcement) => {
										if (!announcement) {
											return EMPTY;
										}

										if (announcement.announced) {
											return EMPTY;
										}

										return this.showDialog(announcement.version);
									}),
								);
							}),
							take(1),
						);
					} else {
						return this.showDialog(announcement.version);
					}
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();

		// TODO Autologout is disabled untill we have looked into if we should fetch tasks again after logout/login from autologout -> https://dev.azure.com/corilusnv/intocare/_workitems/edit/219296
		// this.autoLogoutService.autoLogoutAfterSeconds(300).subscribe();

		combineLatest([this.icOrganisation.organisation$, this.isAuthenticated$])
			.pipe(
				tap(([organisation, authenticated]) => {
					if (authenticated) {
						this.debugReduxStore.init(organisation);
					} else {
						this.debugReduxStore.clearIotSubscriptions();
					}
				}),
				catchError(() => {
					this.handleIncorrectAppState();
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();

		this.isAuthenticated$
			.pipe(
				switchMap((authenticated) => {
					/**
					 * If we are authenticated, start listening to the resume event and the newAnnouncementsIotEvent
					 * If we logout, stop listening to the resume event and the newAnnouncementsIotEvent
					 */
					const unsubscribe = new Subject<void>();
					if (authenticated) {
						this.matomoService.load();
						this.store.dispatch(AnnouncementsActions.getAnnouncements());
						return merge(this.cordova.resume$(), this.announcementsModel.newAnnouncementsIotEvent()).pipe(
							tap(() => this.store.dispatch(AnnouncementsActions.getAnnouncements())),
							takeUntil(unsubscribe),
						);
					} else {
						unsubscribe.next();
						return EMPTY;
					}
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}

	private showResidentsForNfcTagReads() {
		return combineLatest([this.cordova.nfcReady$(), this.icOrganisation.organisation$, this.isAuthenticated$]).pipe(
			switchMap(([nfcReady, organisation, authenticated]) =>
				nfcReady && organisation && authenticated
					? this.nfc.listen(`pridiktiv/${organisation}`).pipe(map((data) => [data, organisation]))
					: EMPTY,
			),
			// @ts-expect-error TS2345
			map(([nfcEvents, organisation]) => {
				const validMessage = (nfcEvents as NFCEvent).tag.ndefMessage.find((nfcEvent) => {
					const type = this.nfc.bytesToString(nfcEvent.type);

					return type === `pridiktiv/${organisation}`;
				});

				if (validMessage && validMessage.payload) {
					return this.nfc.bytesToString(validMessage.payload);
				}
			}),
			filter<string>(Boolean),
			switchMap((roomNr) => {
				this.store.dispatch(
					new FiltersActions.NfcTagScanned({
						room: roomNr,
					}),
				);

				return this.store
					.select((state) => state.data.patients || [])
					.pipe(
						map((residents) => matchNFCToPatient(residents, roomNr)),
						take(1),
					);
			}),
			tap((residentsForRoom) => {
				if (residentsForRoom.length === 1) {
					const [firstResident] = residentsForRoom;
					this.store.dispatch(new FiltersActions.Clear());
					this.router.navigate([firstResident.id, "overview"]);
				} else if (residentsForRoom.length > 1) {
					const [firstResident] = residentsForRoom;
					// @ts-expect-error TS2322
					this.store.dispatch(new FiltersActions.SetRoomOnNfcScan({ room: firstResident.room }));
					this.router.navigate(["/home"]);
				} else {
					this.toast.error(this.translateService.instant("nfc.resident_not_found"), { ms: 2000, forceClear: true });
				}
			}),
			takeUntilDestroyed(this.destroyRef),
		);
	}

	private showDialog(announcementVersion: string) {
		if (this.dialog && !this.dialog.openDialogs.length && !this.icAppState.appInError) {
			const dialog = this.dialog.open(DialogComponent, {
				data: {
					content: "general.dialogs.announcement.body",
					title: "general.dialogs.announcement.header",
					cancelButtonText: "general.dialogs.announcement.later",
					confirmButtonText: "general.dialogs.announcement.read",
				},
				panelClass: "announcement-dialog",
			});
			return dialog.afterClosed().pipe(
				tap((result) => {
					this.store.dispatch(AnnouncementsActions.setAnnouncementAcknowledged({ version: announcementVersion }));
					if (result) {
						this.router.navigate(["/home/announcements"]);
					}
				}),
			);
		} else {
			return EMPTY;
		}
	}

	ngOnDestroy() {
		this.debugReduxStore.clearIotSubscriptions();
	}
}
